import React from 'react';
import clsx from 'clsx';
import { Icon, Element } from '../../atoms';
import { useFieldContext } from '../field/context';
import * as styles from './checkbox.styles';

export const CheckboxContext = React.createContext({ value: undefined, name: undefined, onChange: undefined });

const stopPropagation = (evt) => {
  evt.stopPropagation();
};

/** @type {import(".").CheckboxType} */
const Checkbox = React.forwardRef(({ className, checked: propChecked, value, indeterminated, onChange, children, ...props }, ref) => {
  const { disabled, name, ...contextValues } = useFieldContext(props);

  const checked = propChecked || contextValues?.value?.includes?.(value);

  const customOnChange = contextValues.onChange
    ? (evt) => {
        const { target } = evt;
        const newValue = contextValues.value.includes(target.value)
          ? contextValues.value.filter((x) => {
              return target.value !== x;
            })
          : contextValues.value.concat([target.value]);
        evt.target.value = newValue;
        const newEvent = {
          ...evt,
          target: {
            name: target.name,
            value: newValue,
            source: target,
          },
        };
        contextValues.onChange(newEvent);
      }
    : undefined;

  return (
    <Element
      as="label"
      isInteractive
      className={clsx(styles.checkbox, className, { [styles.variants.checked]: checked || indeterminated })}
      withEnterAsClick
      role="checkbox"
      tabIndex="0"
      disabled={disabled}
      {...props}
    >
      <Element
        as="input"
        readOnly
        disabled={disabled}
        name={name}
        style={{ display: 'none' }}
        type="checkbox"
        value={value}
        ref={ref}
        checked={checked ?? false}
        onChange={disabled ? undefined : onChange || customOnChange}
        onClick={stopPropagation}
      />
      <Element className={styles.components.iconLayout}>
        <Element
          disabled={disabled}
          className={clsx(styles.components.icon, {
            [styles.variants.checked]: checked || indeterminated,
          })}
        >
          <Icon disabled={disabled} name={checked ? 'check' : 'minus'} size="xs" />
        </Element>
      </Element>
      {children && (
        <Element disabled={disabled} className={clsx(styles.components.label, { [styles.variants.checked]: checked || indeterminated })}>
          {children}
        </Element>
      )}
    </Element>
  );
});

if (process.env.NODE_ENV !== 'production') {
  Checkbox.displayName = 'Checkbox';
}

export default Checkbox;
